import React from "react";

export const PageHeader = ({ title, subtitle, actions, testId }) => (
  <div className="flex items-start justify-between gap-6 flex-wrap" data-testid={testId || "page-header"}>
    <div>
      <h1 className="text-[26px] font-semibold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

export const KpiCard = ({ label, value, hint, tone = "blue", icon: Icon, testId, onClick, className = "" }) => {
  const tones = {
    blue:  "bg-[#e6f0fb] text-[#1565C0]",
    green: "bg-[#e6f4ea] text-[#1B5E20]",
    amber: "bg-[#fef4e2] text-[#915907]",
    red:   "bg-[#fdecec] text-[#B71C1C]",
    slate: "bg-slate-100 text-slate-700",
  };
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={`kpi-card text-left w-full ${onClick ? "cursor-pointer hover:shadow-md hover:border-[#1976D2]/40 transition-all focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" : ""} ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-[12px] text-slate-500 font-medium tracking-wide uppercase">{label}</div>
        {Icon && <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-4 h-4" /></div>}
      </div>
      <div className="mt-3 stat-number text-3xl text-slate-900">{value}</div>
      {hint && <div className="text-[12px] text-slate-500 mt-1">{hint}</div>}
    </Tag>
  );
};

export const SectionCard = ({ title, actions, children, testId, id }) => (
  <div id={id} className="bg-white border border-slate-200 rounded-xl" data-testid={testId}>
    {(title || actions) && (
      <div className="px-5 py-3.5 flex items-center justify-between border-b border-slate-200">
        {title && <div className="font-semibold text-slate-800">{title}</div>}
        {actions}
      </div>
    )}
    <div>{children}</div>
  </div>
);

export const Chip = ({ tone = "slate", children, testId }) => (
  <span className={`chip chip-${tone}`} data-testid={testId}>{children}</span>
);

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="text-center py-14 px-6" data-testid="empty-state">
    {Icon && <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>}
    <div className="font-semibold text-slate-800">{title}</div>
    {description && <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{description}</div>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const StatusPill = ({ status }) => {
  const map = {
    "Waiting": "amber",
    "In Consultation": "blue",
    "Completed": "green",
    "Ordered": "amber",
    "Collected": "blue",
    "In Progress": "blue",
    "Awaiting Verification": "amber",
    "Verified": "green",
    "Rejected": "red",
    "Pending": "amber",
    "Dispensed": "green",
    "Unpaid": "amber",
    "Partially Paid": "blue",
    "Paid": "green",
    "active": "green",
    "trial": "blue",
    "grace": "amber",
    "suspended": "red",
    "critical": "red",
    "normal": "green",
    "warning": "amber",
  };
  return <span className={`chip chip-${map[status] || "slate"}`}>{status}</span>;
};

// Doctor-facing lab status: hides internal lab workflow states behind
// the 3 outcomes that matter to a clinician: Waiting → In progress → Ready.
export const doctorLabView = (status) => {
  if (status === "Verified") return { label: "Ready", tone: "green" };
  if (status === "Rejected") return { label: "Rejected", tone: "red" };
  if (["Collected", "In Progress", "Awaiting Verification"].includes(status)) return { label: "In progress", tone: "blue" };
  // "Ordered" (default) — specimen not yet collected
  return { label: "Waiting", tone: "amber" };
};

export const DoctorLabStatus = ({ status }) => {
  const v = doctorLabView(status);
  return <span className={`chip chip-${v.tone}`} data-testid={`doctor-lab-status-${v.label.toLowerCase().replace(/\s+/g,"-")}`}>{v.label}</span>;
};
